import type { Point } from '../../../types';
import { DrawShape } from './DrawShape';
import type { DrawShapeStyle, Size } from './types';

export class DrawRectangle extends DrawShape {
  readonly type = 'rectangle' as const;

  constructor(
    id: string,
    position: Point,
    size: Size,
    style: DrawShapeStyle
  ) {
    super(id, position, size, style);
  }

  containsPoint(p: Point): boolean {
    const { x, y } = this.position;
    const { width, height } = this.size;
    const threshold = 10; // pixels from edge to detect (both inward and outward)

    // Check if point is within the expanded outer bounds
    const inExpandedOuter =
      p.x >= x - threshold && p.x <= x + width + threshold &&
      p.y >= y - threshold && p.y <= y + height + threshold;
    if (!inExpandedOuter) return false;

    // Check if point is deep inside (beyond the inner grab zone)
    const insideInner =
      p.x > x + threshold &&
      p.x < x + width - threshold &&
      p.y > y + threshold &&
      p.y < y + height - threshold;

    // Return true if in the border zone (expanded outer but not deep inner)
    return !insideInner;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { x, y } = this.position;
    const { width, height } = this.size;
    const { fill, stroke, strokeWidth, opacity, cornerRadius } = this.style;

    ctx.save();
    ctx.globalAlpha = opacity;

    // Draw rounded rectangle
    const radius = cornerRadius ?? 0;
    ctx.beginPath();
    if (radius > 0) {
      const r = Math.min(radius, width / 2, height / 2);
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + width - r, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + r);
      ctx.lineTo(x + width, y + height - r);
      ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
      ctx.lineTo(x + r, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
    } else {
      ctx.rect(x, y, width, height);
    }
    ctx.closePath();

    // Fill
    if (fill && fill !== 'transparent') {
      ctx.fillStyle = fill;
      ctx.fill();
    }

    // Stroke
    if (stroke && strokeWidth > 0) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = strokeWidth;
      ctx.stroke();
    }

    ctx.restore();
  }

  static fromJSON(data: { id: string; position: Point; size: Size; style: DrawShapeStyle }): DrawRectangle {
    return new DrawRectangle(data.id, data.position, data.size, data.style);
  }
}
